import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import {
  AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem, ListItemIcon, Box,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SpaOutlinedIcon from '@mui/icons-material/SpaOutlined';
import LanguageToggle from '@/components/LanguageToggle';
import { useAuth } from '@/hooks/useAuth';
import { logout as logoutAction } from '@/store/authSlice';
import { authApi } from '@/services/api';

// Routes that manage their own full-screen chrome (auth, report, checkout).
//
// `/face-capture` is NOT one of them: the camera is rendered inline inside the
// page sheet, not as a full-screen overlay, so hiding the bar just left the
// title floating on a plain background while every neighbouring screen kept
// its navy chrome.
const HIDE_PREFIXES = ['/login', '/otp', '/welcome', '/signup', '/clinic-entry', '/report', '/checkout'];

/**
 * Top bar for the client app, mirroring the clinic app: optional back button,
 * a dashboard shortcut, language toggle and an account menu (profile + logout).
 */
export default function HeaderBar({ title, showBack = false, actions = null }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useAuth();
  const isRtl = i18n.dir() === 'rtl';
  const [anchor, setAnchor] = useState(null);

  const hidden = !isAuthenticated || HIDE_PREFIXES.some((p) => pathname.startsWith(p));

  const BackIcon = isRtl ? ArrowForwardIcon : ArrowBackIcon;
  const initial = (user?.firstName || '?').charAt(0).toUpperCase();

  const handleLogout = async () => {
    setAnchor(null);
    try { await authApi.logoutRequest(); } catch { /* ignore network errors on logout */ }
    dispatch(logoutAction());
    navigate('/login', { replace: true });
  };

  if (hidden) return null;

  return (
    <AppBar position="sticky" color="primary" elevation={0}>
      <Toolbar>
        {showBack && (
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label={t('common.back')}>
            <BackIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          {!showBack && <SpaOutlinedIcon fontSize="small" />}
          <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
            {title || t('common.appName')}
          </Typography>
        </Box>
        {actions}
        {pathname !== '/dashboard' && (
          <IconButton color="inherit" onClick={() => navigate('/dashboard')} aria-label={t('common.dashboard')} title={t('common.dashboard')}>
            <SpaceDashboardIcon />
          </IconButton>
        )}
        <LanguageToggle />
        <IconButton color="inherit" onClick={(e) => setAnchor(e.currentTarget)} aria-label="account">
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark', fontSize: 14 }}>{initial}</Avatar>
        </IconButton>
        <Menu
          anchorEl={anchor}
          open={Boolean(anchor)}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: isRtl ? 'left' : 'right' }}
        >
          <MenuItem onClick={() => { setAnchor(null); navigate('/profile'); }}>
            <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
            {t('common.editProfile')}
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
            {t('common.logout')}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
